import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDrop, useDrag } from 'react-dnd';
import { api } from '../../app/http-common';
import SendSuggestionsModal from '../../components/SendSuggestionsModal/SendSuggestionsModal';
import { useCanEdit, useIsAdmin } from '../../hooks/useFrontegg';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { clipboardWriteDeferred } from '../../utils/clipboard';
import './BucketSidebar.css';

export const STORY_BUCKET_TYPE = 'STORY_TO_BUCKET';
const SIDEBAR_BUCKET_REORDER_TYPE = 'SIDEBAR_BUCKET_REORDER';
const LOCAL_STORAGE_SIDEBAR_ORDER_KEY = 'alchemiq_sidebar_bucket_order';

const SIDEBAR_WIDTH_COLLAPSED = 280;
const SIDEBAR_WIDTH_EXPANDED = 500;

const STATUS_COLORS = {
  suggestion: '#2196F3',
  accepted: '#9C27B0',
  in_work: '#FF9800',
  completed: '#009688',
  sent_to_publisher: '#9E9E9E',
  published: '#4CAF50',
  rejected: '#F44336',
  deleted: '#BDBDBD',
};

const STATUS_LABELS = {
  suggestion: 'Suggestion',
  accepted: 'Accepted',
  in_work: 'In Progress',
  completed: 'Completed',
  sent_to_publisher: 'Sent',
  published: 'Published',
  rejected: 'Rejected',
  deleted: 'Deleted',
};

const STATUS_FORWARD_MAP = {
  suggestion: 'accepted',
  accepted: 'in_work',
  in_work: 'completed',
  completed: 'sent_to_publisher',
  sent_to_publisher: 'published',
};

const formatIsraelTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleString('en-GB', {
      timeZone: 'Asia/Jerusalem',
      day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
};

// Individual bucket drop target (also draggable for reorder)
const BucketDropItem = ({ bucket, index, onDrop, onReorder, addedBucketId, rejectedBucketId, rejectionMessage, isAdmin, isExpanded, onExpand }) => {
  const ref = useRef(null);

  const [{ isOver }, drop] = useDrop({
    accept: [STORY_BUCKET_TYPE, SIDEBAR_BUCKET_REORDER_TYPE],
    drop: (item, monitor) => {
      if (item.type === SIDEBAR_BUCKET_REORDER_TYPE) return;
      onDrop(bucket._id, item.storyId);
    },
    hover: (dragged, monitor) => {
      if (dragged.type !== SIDEBAR_BUCKET_REORDER_TYPE) return;
      if (dragged.index === index) return;
      onReorder(dragged.index, index);
      dragged.index = index;
    },
    collect: (monitor) => ({ isOver: monitor.isOver() && monitor.getItemType() === STORY_BUCKET_TYPE }),
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: SIDEBAR_BUCKET_REORDER_TYPE,
    item: () => ({ type: SIDEBAR_BUCKET_REORDER_TYPE, index }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  preview(drop(ref));

  const isRejected = rejectedBucketId === bucket._id;
  const isAdded = addedBucketId === bucket._id;

  return (
    <div
      ref={ref}
      className={`bucket-item ${isOver ? 'drag-over' : ''} ${isAdded ? 'just-added' : ''} ${isRejected ? 'bucket-rejected' : ''} ${isDragging ? 'bucket-item-dragging' : ''} ${isExpanded ? 'bucket-item-expanded' : ''}`}
      onClick={() => onExpand(bucket._id)}
      style={{ cursor: 'pointer' }}
    >
      <div className="bucket-item-header">
        <span ref={drag} className="bucket-item-drag-handle" title="Drag to reorder" onClick={e => e.stopPropagation()}><DragIndicatorIcon style={{ fontSize: 16 }} /></span>
        <div className="bucket-item-name">
          <span className="bucket-expand-indicator">{isExpanded ? <ExpandMoreIcon style={{ fontSize: 16 }} /> : <ChevronRightIcon style={{ fontSize: 16 }} />}</span>
          {/* Corrections spec 2.7 / 7.3 — this narrow panel is the ONE surface
              that uses the short display name. Every other surface shows the
              complete bucket name. Falls back to the full name when unset, so
              an unconfigured bucket looks exactly as it does today. The full
              name stays available on hover for disambiguation. */}
          <span title={bucket.name}>{bucket.shortDisplayName || bucket.name}</span>
        </div>
        {isAdmin && (
          <a
            href={`/admin2/publisher-buckets/${bucket._id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="bucket-admin-link"
            title="Open in Admin Panel"
            onClick={(e) => e.stopPropagation()}
          >
            <SettingsOutlinedIcon style={{ fontSize: 16 }} />
          </a>
        )}
      </div>
      <div className="bucket-item-meta">
        <span className="bucket-item-lang">{bucket.targetLanguage}</span>
        <span className="bucket-item-count">{bucket.totalStories || 0}</span>
      </div>
      {!isExpanded && bucket.storyCounts && Object.keys(bucket.storyCounts).length > 0 && (
        <div className="bucket-status-dots">
          {Object.entries(bucket.storyCounts)
            .filter(([s]) => s !== 'deleted')
            .map(([status, count]) => (
            <span
              key={status}
              className="bucket-status-dot"
              title={`${status}: ${count}`}
              style={{ backgroundColor: STATUS_COLORS[status] || '#ccc' }}
            >
              {count}
            </span>
          ))}
        </div>
      )}
      {isAdded && (
        <div className="bucket-added-flash">Added!</div>
      )}
      {isRejected && (
        <div className="bucket-rejected-flash">
          {(rejectionMessage && typeof rejectionMessage === 'object')
            ? (
              <>
                {rejectionMessage.text}
                {rejectionMessage.url && (
                  <>
                    {' '}
                    <a
                      href={rejectionMessage.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: 'inherit', textDecoration: 'underline' }}
                    >
                      View publication
                    </a>
                  </>
                )}
              </>
            )
            : (rejectionMessage || 'Already exists')}
        </div>
      )}
    </div>
  );
};

// Inline story row inside expanded bucket
const ExpandedStoryRow = ({ item, index, onAdvance }) => {
  const title = item.story?.title || `Story ${item.storyId?.toString().slice(-6)}`;
  const nextStatus = STATUS_FORWARD_MAP[item.status];
  const articles = item.articleCountSnapshot || item.story?.articleCount || 0;

  return (
    <div className={`expanded-story-row ${item.isArchived ? 'expanded-story-archived' : ''}`}>
      <div className="expanded-story-title" title={title}>
        <span className="expanded-story-number">#{index + 1}</span>
        {title}
      </div>
      <div className="expanded-story-meta">
        <span
          className="expanded-story-badge"
          style={{ backgroundColor: STATUS_COLORS[item.status] || '#ccc', cursor: nextStatus ? 'pointer' : 'default' }}
          title={nextStatus ? `Click to advance to ${STATUS_LABELS[nextStatus]}` : item.status}
          onClick={(e) => { e.stopPropagation(); if (nextStatus) onAdvance(item._id); }}
        >
          {STATUS_LABELS[item.status] || item.status}
        </span>
        <span className="expanded-story-articles">{articles} art.</span>
        <span className="expanded-story-time">{formatIsraelTime(item.createdAt)}</span>
      </div>
    </div>
  );
};

const BucketSidebar = ({ isOpen, onToggle, onStoryAdded, onWidthChange, collectionScope }) => {
  const canEdit = useCanEdit();
  const isAdmin = useIsAdmin();
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addedBucketId, setAddedBucketId] = useState(null);
  const [rejectedBucketId, setRejectedBucketId] = useState(null);
  const [rejectionMessage, setRejectionMessage] = useState(null);
  const [error, setError] = useState(null);
  const [bucketOrder, setBucketOrder] = useState(() => {
    try { return JSON.parse(localStorage.getItem(LOCAL_STORAGE_SIDEBAR_ORDER_KEY)) || []; }
    catch { return []; }
  });

  // Expanded bucket state
  const [expandedBucketId, setExpandedBucketId] = useState(null);
  const [expandedStories, setExpandedStories] = useState([]);
  const [expandedLoading, setExpandedLoading] = useState(false);
  const [expandedSearch, setExpandedSearch] = useState('');
  const [expandedArchiveFilter, setExpandedArchiveFilter] = useState('hide');
  const [actionToast, setActionToast] = useState(null);
  const actionToastTimerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // No-contact-point modal state
  // The bucket whose Send link confirmation is open, by id.
  const [sendModalBucket, setSendModalBucket] = useState(null);

  const sidebarWidth = expandedBucketId ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED;

  // Notify parent of width changes
  useEffect(() => {
    if (onWidthChange) onWidthChange(isOpen ? sidebarWidth : 0);
  }, [isOpen, sidebarWidth, onWidthChange]);

  const loadBuckets = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getPublisherBuckets();
      setBuckets(data.buckets || []);
    } catch (err) {
      console.error('Failed to load buckets:', err);
      setError('Failed to load buckets');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadExpandedStories = useCallback(async (bucketId, query, archiveFilter) => {
    try {
      setExpandedLoading(true);
      const params = { limit: 100, sort: 'sort_order', query: query || undefined };
      const af = archiveFilter || expandedArchiveFilter || 'hide';
      if (af !== 'hide') params.include_archived = af;
      const data = await api.getBucketStories(bucketId, params);
      setExpandedStories(data.items || []);
    } catch (err) {
      console.error('Failed to load bucket stories:', err);
      setExpandedStories([]);
    } finally {
      setExpandedLoading(false);
    }
  }, [expandedArchiveFilter]);

  // Apply saved order to buckets
  const orderedBuckets = (() => {
    if (bucketOrder.length === 0) return buckets;
    const map = {};
    for (const b of buckets) map[b._id] = b;
    const ordered = bucketOrder.filter(id => map[id]).map(id => map[id]);
    for (const b of buckets) {
      if (!bucketOrder.includes(b._id)) ordered.push(b);
    }
    return ordered;
  })();

  useEffect(() => {
    if (isOpen && canEdit) {
      loadBuckets();
    }
    if (!isOpen) {
      setExpandedBucketId(null);
      setExpandedStories([]);
      setExpandedSearch('');
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    }
  }, [isOpen, canEdit, loadBuckets]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      if (actionToastTimerRef.current) clearTimeout(actionToastTimerRef.current);
    };
  }, []);

  const handleExpand = useCallback((bucketId) => {
    if (expandedBucketId === bucketId) {
      // Collapse
      setExpandedBucketId(null);
      setExpandedStories([]);
      setExpandedSearch('');
      setExpandedArchiveFilter('hide');
    } else {
      // Expand
      setExpandedBucketId(bucketId);
      setExpandedSearch('');
      setExpandedArchiveFilter('hide');
      loadExpandedStories(bucketId, '', 'hide');
    }
  }, [expandedBucketId, loadExpandedStories]);

  const handleExpandedSearch = useCallback((value) => {
    setExpandedSearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      if (expandedBucketId) loadExpandedStories(expandedBucketId, value);
    }, 350);
  }, [expandedBucketId, loadExpandedStories]);

  const handleAdvanceStatus = useCallback(async (bucketStoryId) => {
    try {
      await api.advanceBucketStoryStatus(bucketStoryId);
      // Refresh the expanded list
      if (expandedBucketId) loadExpandedStories(expandedBucketId, expandedSearch);
    } catch (err) {
      console.error('Failed to advance status:', err);
    }
  }, [expandedBucketId, expandedSearch, loadExpandedStories]);

  const showActionToast = useCallback((msg) => {
    setActionToast(msg);
    clearTimeout(actionToastTimerRef.current);
    actionToastTimerRef.current = setTimeout(() => setActionToast(null), 2000);
  }, []);

  const handleCopySuggestions = useCallback(async (bucketId) => {
    try {
      // Auto-create snapshot, then copy suggestion titles to clipboard
      const result = await api.createSuggestionList(bucketId, true, 'copy_list');
      const items = result.list?.items || [];
      const text = items.map((it, i) => `${it.suggestionNumber || i + 1}. ${it.titleSnapshot || 'Untitled'}`).join('\n');
      await navigator.clipboard.writeText(text);
      showActionToast('Suggestions copied!');
    } catch (err) {
      console.error('Failed to copy suggestions:', err);
      showActionToast('Failed to copy');
    }
  }, [showActionToast]);

  const handleCopyPublicLink = useCallback(async (bucketId) => {
    try {
      const url = await clipboardWriteDeferred(async () => {
        const result = await api.createSuggestionList(bucketId, true, 'copy_link');
        const token = result.list?.publicToken;
        if (token) return `${window.location.origin}/suggestions/${token}`;
        return '';
      });
      if (url) showActionToast('Link copied!');
    } catch (err) {
      console.error('Failed to copy public link:', err);
      showActionToast('Failed to copy link');
    }
  }, [showActionToast]);

  // Send link, from the Stories sidebar. Opens the SHARED confirmation rather
  // than sending immediately.
  //
  // It used to fire the send straight from the button, so nobody saw who it
  // was going to and there was no way to choose a channel. Adi asked for
  // WhatsApp from a bucket page on 5 November; three pages send this same
  // link, and a copy of the dialog in each is how a later change reaches one
  // of them and misses two (CLAUDE.md Gotcha 25).
  const handleSendSuggestions = useCallback((bucketId) => {
    setSendModalBucket(bucketId);
  }, []);

  const handleDrop = useCallback(async (bucketId, storyId) => {
    if (!storyId) return;
    try {
      // Corrections spec 5.2 — the same-client duplicate rule applies to Story
      // Feed adds too, not only to Bucket Stories. A story already ACTIVE in
      // another bucket of this client is blocked here rather than quietly
      // producing a second client-facing copy. The existing eligibility gate in
      // addStoryToBucket covers "already published by the client", which is a
      // different question from "already sitting in a sibling bucket".
      //
      // Suggestion-elsewhere is deliberately NOT blocked here: 5.2 wants a
      // Copy anyway / Transfer choice for that case, and this narrow panel has
      // no room for it — the operator gets the reason and can act in Bucket
      // Stories, which does offer the choice.
      try {
        const dup = await api.checkBucketDuplicate(bucketId, storyId, { silent: true });
        if (dup && dup.allowed === false && !dup.conflict?.sameTarget && !dup.requiresChoice) {
          const err = new Error('duplicate');
          err.response = {
            status: 422,
            data: {
              reason: 'same_client_active',
              error: `Already active in "${dup.conflict?.bucketName}" for this publisher`,
            },
          };
          throw err;
        }
      } catch (checkErr) {
        // Re-throw our own signal; a failed CHECK must never block a legal add.
        if (checkErr?.response?.data?.reason === 'same_client_active') throw checkErr;
      }

      // Drag-and-drop from the stories feed is the same manual add as the row
      // action, so it records the same scope.
      await api.addStoryToBucket(bucketId, storyId, collectionScope);
      setAddedBucketId(bucketId);
      setTimeout(() => setAddedBucketId(null), 1200);
      loadBuckets();
      if (expandedBucketId === bucketId) loadExpandedStories(bucketId, expandedSearch);
      if (onStoryAdded) onStoryAdded(bucketId, storyId);
    } catch (err) {
      // 409 = already in bucket. 422 = Publisher Bucket Intelligence rejection
      // (client already published, or bucket excludes competitors). Both flash
      // the reject animation, but with distinct copy so editors know why.
      if (err.response?.status === 409 || err.response?.status === 422) {
        let msg = 'Already exists';
        if (err.response.status === 422) {
          const reasonMap = {
            client_already_published: 'Detected as published by publisher',
            competitor_excluded: 'Published by direct competitor',
            bucket_inactive: 'Bucket inactive',
            // Spec 5.2 — already active in a sibling bucket of the same client.
            same_client_active: err.response.data?.error,
          };
          msg = reasonMap[err.response.data?.reason] || err.response.data?.error || 'Not eligible for this bucket';
        }
        // Spec §5.1 — when the rejection is `client_already_published`, the
        // backend returns the specific Publications row (associationId, url,
        // headline). Surface a "View publication" link so the editor can jump
        // straight to the article that caused the block.
        const publication = err.response.data?.publication || null;
        const rejectionPayload = (publication && publication.articleUrl && err.response.data?.reason === 'client_already_published')
          ? { text: msg, url: publication.articleUrl }
          : msg;
        setRejectedBucketId(bucketId);
        setRejectionMessage(rejectionPayload);
        // Give the editor time to click the link — extend the flash when a link is present.
        setTimeout(() => { setRejectedBucketId(null); setRejectionMessage(null); }, publication ? 6000 : 2400);
      } else {
        console.error('Failed to add story to bucket:', err);
      }
    }
  }, [loadBuckets, onStoryAdded, expandedBucketId, expandedSearch, loadExpandedStories]);

  const handleReorder = useCallback((fromIndex, toIndex) => {
    const ids = orderedBuckets.map(b => b._id);
    const [moved] = ids.splice(fromIndex, 1);
    ids.splice(toIndex, 0, moved);
    setBucketOrder(ids);
    localStorage.setItem(LOCAL_STORAGE_SIDEBAR_ORDER_KEY, JSON.stringify(ids));
  }, [orderedBuckets]);

  if (!canEdit) return null;

  return (
    <>
      <button
        className={`bucket-sidebar-toggle ${isOpen ? 'open' : ''}`}
        onClick={onToggle}
        title={isOpen ? 'Hide Publisher Buckets' : 'Show Publisher Buckets'}
        style={isOpen ? { right: sidebarWidth } : undefined}
      >
        <InventoryOutlinedIcon style={{ fontSize: 18 }} />
      </button>
      {isOpen && (
        <div className={`bucket-sidebar ${expandedBucketId ? 'bucket-sidebar-wide' : ''}`}>
          <div className="bucket-sidebar-header">
            <h3>Publisher Buckets</h3>
          </div>
          {loading ? (
            <div className="bucket-sidebar-loading">Loading...</div>
          ) : error ? (
            <div className="bucket-sidebar-error">{error}</div>
          ) : buckets.length === 0 ? (
            <div className="bucket-sidebar-empty">No buckets yet. Create one in Admin.</div>
          ) : (
            <div className="bucket-list">
              {orderedBuckets.map((bucket, idx) => (
                <React.Fragment key={bucket._id}>
                  <BucketDropItem
                    bucket={bucket}
                    index={idx}
                    onDrop={handleDrop}
                    onReorder={handleReorder}
                    addedBucketId={addedBucketId}
                    rejectedBucketId={rejectedBucketId}
                    rejectionMessage={rejectionMessage}
                    isAdmin={isAdmin}
                    isExpanded={expandedBucketId === bucket._id}
                    onExpand={handleExpand}
                  />
                  {expandedBucketId === bucket._id && (
                    <div className="expanded-bucket-panel">
                      <div className="expanded-bucket-actions">
                        <button
                          className="expanded-action-btn"
                          title="Copy list"
                          onClick={(e) => { e.stopPropagation(); handleCopySuggestions(bucket._id); }}
                        >
                          <ContentCopyOutlinedIcon style={{ fontSize: 14 }} /> Copy list
                        </button>
                        <button
                          className="expanded-action-btn"
                          title="Copy link"
                          onClick={(e) => { e.stopPropagation(); handleCopyPublicLink(bucket._id); }}
                        >
                          <LinkOutlinedIcon style={{ fontSize: 14 }} /> Copy link
                        </button>
                        <button
                          className="expanded-action-btn"
                          title="Send link"
                          onClick={(e) => { e.stopPropagation(); handleSendSuggestions(bucket._id); }}
                        >
                          <SendOutlinedIcon style={{ fontSize: 14 }} /> Send link
                        </button>
                      </div>
                      {actionToast && <div className="expanded-action-toast">{actionToast}</div>}
                      <div className="expanded-bucket-search">
                        <input
                          type="text"
                          placeholder="Search stories..."
                          value={expandedSearch}
                          onChange={e => handleExpandedSearch(e.target.value)}
                          onClick={e => e.stopPropagation()}
                        />
                      </div>
                      <div className="expanded-bucket-archive-filter">
                        <select
                          value={expandedArchiveFilter}
                          onChange={e => {
                            const v = e.target.value;
                            setExpandedArchiveFilter(v);
                            loadExpandedStories(expandedBucketId, expandedSearch, v);
                          }}
                          onClick={e => e.stopPropagation()}
                        >
                          <option value="hide">Hide archived</option>
                          <option value="include">Include archived</option>
                          <option value="only">Only archived</option>
                        </select>
                      </div>
                      {expandedLoading ? (
                        <div className="expanded-bucket-loading">Loading...</div>
                      ) : expandedStories.length === 0 ? (
                        <div className="expanded-bucket-empty">
                          {expandedSearch ? 'No matching stories' : 'No stories in this bucket'}
                        </div>
                      ) : (
                        <div className="expanded-bucket-stories">
                          {expandedStories.map((item, idx) => (
                            <ExpandedStoryRow
                              key={item._id}
                              item={item}
                              index={idx}
                              onAdvance={handleAdvanceStatus}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      )}
      {/* Send link. The SHARED confirmation, mounted here, on BucketStories
          and on BucketManager, so a channel added later cannot reach one page
          and miss the other two. */}
      <SendSuggestionsModal
        bucketId={sendModalBucket}
        isOpen={Boolean(sendModalBucket)}
        onClose={() => setSendModalBucket(null)}
        onResult={(message) => showActionToast(message)}
      />
    </>
  );
};

export default BucketSidebar;
